import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { Dashboard as DashboardIcon, TrendingUp as TrendingUpIcon, Receipt as ReceiptIcon, AccountBalance as AccountBalanceIcon } from '@mui/icons-material';
import { dashboardAPI, vendorAPI, categoryAPI } from '../api/api';
import CategoryDistributionChart from '../components/CategoryDistributionChart';
import { getColorForChartType } from '../utils/colorUtils';

const ChartView = () => {
  const [timeRange, setTimeRange] = useState('monthly');
  const [selectedVendorId, setSelectedVendorId] = useState(''); // New state for vendor filter
  const [selectedCategoryId, setSelectedCategoryId] = useState(''); // New state for category filter
  const [costData, setCostData] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [vendorData, setVendorData] = useState([]);
  const [categoryWiseData, setCategoryWiseData] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]); // New state for categories
  const [stats, setStats] = useState({
    totalCost: 0,
    invoicesCount: 0,
    vendorsCount: 0,
    categoriesCount: 0
  });

  // Create a deduplicated list of categories for the dropdown to prevent showing multiple entries with the same standard name
  const uniqueCategories = categories.reduce((acc, category) => {
    const displayName = category.standard_name && category.standard_name.trim() !== '' 
      ? category.standard_name 
      : category.name;
    
    // Check if this display name already exists in our accumulator
    const existingCategory = acc.find(item => 
      (item.standard_name && item.standard_name.trim() !== '' ? item.standard_name : item.name) === displayName
    );
    
    // Only add if we don't already have a category with the same display name
    if (!existingCategory) {
      acc.push(category);
    }
    
    return acc;
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch dashboard statistics
        const statsResponse = await dashboardAPI.getStats();
        setStats({
          totalCost: statsResponse.data.total_cost || 0,
          invoicesCount: statsResponse.data.total_invoices || 0,
          vendorsCount: statsResponse.data.total_vendors || 0,
          categoriesCount: statsResponse.data.total_categories || 0
        });

        // Fetch cost graph data based on time range, selected vendor, and selected category
        const costResponse = await dashboardAPI.getCostGraph(timeRange, selectedVendorId, selectedCategoryId);
        // The backend now returns an array of objects with period and total_amount
        // Format the period for better display based on the time range
        const formattedCostData = (costResponse.data || []).map(item => ({
          ...item,
          // Format the period based on the time range for better display
          month: new Date(item.period).toLocaleDateString('en-US', 
            timeRange === 'daily' ? { month: 'short', day: 'numeric', year: 'numeric' } :
            timeRange === 'weekly' ? { month: 'short', day: 'numeric' } :
            timeRange === 'monthly' ? { month: 'short', year: 'numeric' } :
            timeRange === 'quarterly' ? { month: 'short', year: 'numeric' } :
            { year: 'numeric' } // yearly
          )
        }));
        setCostData(formattedCostData);

        // Fetch vendor distribution data
        try {
          const vendorResponse = await dashboardAPI.getVendorDistribution();
          // Also fetch all vendors to map standard names
          const vendorsResponse = await vendorAPI.getVendors();
          setVendors(vendorsResponse.data || []);
          
          // Map vendor names to standard names if available
          const mappedVendorData = (vendorResponse.data || []).map(item => {
            const vendor = (vendorsResponse.data || []).find(v => 
              v.name.toLowerCase() === (item.name || '').toLowerCase()
            );
            
            // Use standard name if available, otherwise use the original name
            const displayName = vendor && vendor.standard_name && vendor.standard_name.trim() !== '' 
              ? vendor.standard_name 
              : item.name || 'Unknown';
            
            return {
              ...item,
              name: displayName
            };
          });
          
          setVendorData(mappedVendorData);
        } catch (vendorError) {
          console.error('Error fetching vendor data:', vendorError);
          // Fallback to empty array if endpoint doesn't exist yet
          setVendorData([]);
        }

        // Fetch category-wise data over time with category filter
        try {
          const categoryWiseResponse = await dashboardAPI.getCategoryWiseData(timeRange, selectedCategoryId);
          setCategoryWiseData(categoryWiseResponse.data || []);
        } catch (categoryWiseError) {
          console.error('Error fetching category-wise data:', categoryWiseError);
          setCategoryWiseData([]);
        }

        // Fetch all categories for the category filter dropdown
        try {
          const categoriesResponse = await categoryAPI.getCategories();
          setCategories(categoriesResponse.data || []);
        } catch (categoriesError) {
          console.error('Error fetching categories:', categoriesError);
          setCategories([]);
        }
      } catch (error) {
        console.error('Error fetching chart data:', error);
        
        // Set default values if API call fails
        setStats({
          totalCost: 0,
          invoicesCount: 0,
          vendorsCount: 0,
          categoriesCount: 0
        });
        
        setCostData([]);
        setVendorData([]);
        setCategoryWiseData([]);
        setCategories([]);
      }
    };

    fetchData();
  }, [timeRange, selectedVendorId, selectedCategoryId]);

  // Process category-wise data for the chart
  const processedCategoryWiseData = categoryWiseData.reduce((acc, item) => {
    const period = new Date(item.period).toLocaleDateString('en-US', 
      timeRange === 'daily' ? { month: 'short', day: 'numeric', year: 'numeric' } :
      timeRange === 'weekly' ? { month: 'short', day: 'numeric' } :
      timeRange === 'monthly' ? { month: 'short', year: 'numeric' } :
      timeRange === 'quarterly' ? { month: 'short', year: 'numeric' } :
      { year: 'numeric' } // yearly
    );
    
    const existingPeriod = acc.find(data => data.period === period);
    if (existingPeriod) {
      existingPeriod[item.name] = parseFloat(item.total_amount);
    } else {
      const newDataPoint = { period };
      newDataPoint[item.name] = parseFloat(item.total_amount);
      acc.push(newDataPoint);
    }
    
    return acc;
  }, []);

  // Get all unique category names
  const categoryNames = [...new Set(categoryWiseData.map(item => item.name))];

  // Color palettes for charts are now handled by colorUtils functions

  return (
    <Container maxWidth="xl" sx={{ mt: 2 }}>
      <Box mb={3}>
        <Box display="flex" alignItems="center" mb={2}>
          <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 56, height: 56 }}>
            <TrendingUpIcon />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Chart View
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Visualize your invoice data and spending patterns
            </Typography>
          </Box>
        </Box>
        
        <Box display="flex" justifyContent="flex-end" gap={2}>
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>Vendor</InputLabel>
            <Select
              value={selectedVendorId}
              label="Vendor"
              onChange={(e) => setSelectedVendorId(e.target.value)}
            >
              <MenuItem value="">All Vendors</MenuItem>
              {vendors.map((vendor) => (
                <MenuItem key={vendor.id} value={vendor.id}>
                  {vendor.standard_name && vendor.standard_name.trim() !== '' 
                    ? vendor.standard_name 
                    : vendor.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>Category</InputLabel>
            <Select
              value={selectedCategoryId}
              label="Category"
              onChange={(e) => setSelectedCategoryId(e.target.value)}
            >
              <MenuItem value="">All Categories</MenuItem>
              {uniqueCategories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.standard_name && category.standard_name.trim() !== '' 
                    ? category.standard_name 
                    : category.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              label="Time Range"
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <MenuItem value="quarterly">Quarterly</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main', mr: 2 }}>
                  <ReceiptIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    ${stats.totalCost.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Cost
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'success.light', color: 'success.main', mr: 2 }}>
                  <DashboardIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.invoicesCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Invoices
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'warning.light', color: 'warning.main', mr: 2 }}>
                  <AccountBalanceIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.vendorsCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Vendors
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'info.light', color: 'info.main', mr: 2 }}>
                  <TrendingUpIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {stats.categoriesCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Categories
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Grid */}
      <Grid container spacing={3}>
        {/* Monthly Cost Chart */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Monthly Cost Overview
              </Typography>
              <Box height={400}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={costData}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 50, // Increased bottom margin for rotated labels
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
                    <XAxis 
                      dataKey="month" 
                      angle={-45} 
                      textAnchor="end"
                      height={50}
                      tick={{ fill: '#666', fontSize: 11 }}
                    />
                    <YAxis 
                      tick={{ fill: '#666' }}
                      tickFormatter={(value) => `${value}`}
                    />
                    <Tooltip 
                      formatter={(value) => [`${value}`, 'Cost']}
                      labelFormatter={(label) => `Month: ${label}`}
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e0e0e0', 
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Legend />
                    <Bar 
                      dataKey="total_amount" 
                      name="Cost" 
                      fill={getColorForChartType('Total Cost', 'bar')} 
                      radius={[4, 4, 0, 0]} // Rounded corners for bars
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Category Distribution */}
        <Grid item xs={12} lg={4}>
          <CategoryDistributionChart />
        </Grid>

        {/* Category-wise Spending Over Time - Individual Charts */}
        {categoryNames.map((category, index) => (
          <Grid item xs={12} lg={4} key={category}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  {category} Spending
                </Typography>
                <Box height={300}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={processedCategoryWiseData}
                      margin={{
                        top: 10,
                        right: 20,
                        left: 20,
                        bottom: 50, // Increased bottom margin for rotated labels
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
                      <XAxis 
                        dataKey="period" 
                        angle={-45} 
                        textAnchor="end"
                        height={50}
                        tick={{ fontSize: 10, fill: '#666' }}
                      />
                      <YAxis 
                        tick={{ fill: '#666' }}
                        tickFormatter={(value) => `${value}`}
                      />
                      <Tooltip 
                        formatter={(value) => [`${value}`, 'Amount']}
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e0e0e0', 
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                        }}
                      />
                      <Bar 
                        dataKey={category} 
                        fill={getColorForChartType(category, 'bar')} 
                        name={category}
                        radius={[4, 4, 0, 0]} // Rounded corners for bars
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
        
        {/* Show a message if no category data is available */}
        {categoryNames.length === 0 && (
          <Grid item xs={12} lg={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Category-wise Spending Over Time
                </Typography>
                <Box height={300} display="flex" alignItems="center" justifyContent="center">
                  <Typography variant="body1" color="text.secondary">
                    No category data available
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default ChartView;